import { computed, ref } from "vue";
import * as configCat from "configcat-js";
import { handleError } from "@/utils";

import type { FeatureName } from "@/types";
import { activeFlags } from "@/config";
import { useAuthStore } from "@/stores/userAuth";

const featureFlags = ref<Record<FeatureName, boolean>>({
    isQuotePriceTooltipAEnabled: false,
    isgmailaddonenabled: false,
    isElementHidingEnabled: false,
    isBuiltInChatHidden: false,
    isOrderManagementContractsEnabled: false,
    isMoRBuyersCreditLimitFeatureEnabled: false,
    isDocumentStoreBetaEnabled: false,
    isOrderManagementOnlyOrderFlowEnabled: false,
    isUsLocalisationEnabled: false,
    isOtifTrackingEnabled: false,
    isAllianzAPIHookupEnabled: false,
    isBuyerOrderRequestsEnabled: false,
    isBuyerSupplierPairPaymentTermsEnabled: false,
    isIssueRefundEnabled: false,
    areOpplyDocumentsVisibleToBuyers: false,
    isMorTasksNotesEnabled: false,
    isNewBuyerCreditLimitViewEnabled: false,
    isCreditCheckerEnabled: false,
    isPushOrderToTwoEnabled: false,
    isMorOrderRequestsEnabled: false,
    isBuyerCreditLimitDashboardEnabled: false,
    isOrderTimelineEnabled: false,
    tasksEnhancementEnabled: false,
    hideViewInvoice: false,
    isMorCreditNotesEnabled: false,
    isMorGroupedStatementsEnabled: false,
    masqueradeToNewBuyer: false,
    masqueradeIsolatedOrigin: false,
    isNewMorDashboardEnabled: false,
    isOrdersMigrationEnabled: false,
    isFlattenedMORNavEnabled: false,
    isMorOrderManagementContractsMigrationEnabled: false,
    isNewMorStatementsEnabled: false,
    isMorContractsMigrationEnabled: false,
    isMorSuppliersMigrationEnabled: false,
    isMorBuyersMigrationEnabled: false,
    showUsSaleTax: false,
});

// Cypress runs against a real-config-cat-keyed build (see `.env.local`) and
// pulls live flag values, which can flip the rendered UI out from under E2E
// specs written against a specific variant. Per-flag overrides keep the
// existing specs deterministic on the variant they were written for, without
// breaking other specs that legitimately depend on a rolled-out flag being
// on (the case where production code already assumes the flag is true and we
// haven't deleted it from ConfigCat yet).
//
// Add a flag to `CYPRESS_FLAG_OVERRIDES` only as a temporary measure when an
// E2E suite hasn't been migrated to the new flagged-on variant. Remove the
// override once the suite covers the new variant or the flag is deleted.
// See sc-3041 / sc-3042.
const CYPRESS_FLAG_OVERRIDES: Partial<Record<FeatureName, boolean>> = {
    // sc-2951 grouped order-actions UI: legacy `orders-actions` E2E specs
    // still target `QuickActions` (flag-off branch). Force off in Cypress
    // until the suite is migrated to `OrderActionsPanel` (sc-3042).
    isMorCreditNotesEnabled: false,
};

const isCypressRun = (): boolean =>
    typeof window !== "undefined" &&
    Boolean((window as { Cypress?: unknown }).Cypress);

export const useFeatureFlag = () => {
    const SDK_KEY = import.meta.env.VITE_CONFIGCAT_KEY || "";
    const configCatClient = configCat.getClient(SDK_KEY || "offline");
    const authStore = useAuthStore();

    const loading = computed(() => !!authStore.loaded);

    if (!SDK_KEY) {
        configCatClient.setOffline();
    }

    const delay = (ms: number) =>
        new Promise((resolve) => {
            setTimeout(resolve, ms);
        });

    const checkFeatureAvailability = async (
        flagName: FeatureName | string
    ): Promise<boolean> => {
        if (isCypressRun() && flagName in CYPRESS_FLAG_OVERRIDES) {
            return CYPRESS_FLAG_OVERRIDES[flagName as FeatureName] ?? false;
        }

        if (!authStore.loaded) {
            await delay(500);
            return checkFeatureAvailability(flagName);
        }

        if (!authStore.companyData?.uuid || !authStore.userData?.email)
            return false;

        const userObject = new configCat.User(
            authStore.companyData.uuid,
            authStore.userData.email
        );

        try {
            return await configCatClient.getValueAsync(
                flagName,
                false,
                userObject
            );
        } catch (e) {
            handleError(e);
            return false;
        }
    };

    /**
     * Loops through active flags and checks their availability.
     */
    const checkActiveFlags = async () => {
        if (!activeFlags.length) return;

        const checks = activeFlags.map(async (flagName) => ({
            flagName,
            isFeatureAvailable: await checkFeatureAvailability(flagName),
        }));

        const results = await Promise.all(checks);

        results.forEach((result) => {
            featureFlags.value[result.flagName] = result.isFeatureAvailable;
        });
    };

    const initializeFeatureFlags = async () => {
        await checkActiveFlags();
    };

    const isFeatureFlagEnabled = (flagName: FeatureName) =>
        featureFlags.value[flagName];

    return {
        loading,
        isFeatureFlagEnabled,
        initializeFeatureFlags,
        checkFeatureAvailability,
    };
};
